import { GenericObjectType } from '../types';
import { GenericMap } from './types';

export const findObjectInObjectByKeyValue = (
  obj: GenericObjectType,
  keyName: string,
  keyValue: string
) => {
  return Object.values(obj).find(
    (innerObj: GenericObjectType) => innerObj[keyName] === keyValue
  );
};

export const readEnvVariable = (key: string) =>
  process.env[`REACT_APP_${key}`] || '';

export const snakeToCamelCase = (str: string): string =>
  str.replace(/[^a-zA-Z0-9]+(.)/g, (m: string, letter: string): string =>
    letter.toUpperCase()
  );

export function convertToMap<T extends { id: string | number }>(
  entries: T[]
): GenericMap<T> {
  return (entries || []).reduce((acc, entry) => {
    acc[entry.id] = entry;
    return acc;
  }, {} as GenericMap<T>);
}

export const findAverageOfArray = (arr: Array<number>) => {
  return arr.length ? arr.reduce((p, c) => p + c, 0) / arr.length : 0;
};

export function arraysEqualByKeys(
  arr1: GenericMap<any>[],
  key1: string,
  arr2: GenericMap<any>[],
  key2: string
): boolean {
  if (arr1.length !== arr2.length) return false;

  const values1 = new Set(arr1.map((obj) => obj[key1]));
  const values2 = new Set(arr2.map((obj) => obj[key2]));

  if (values1.size !== values2.size) return false;

  return [...values1].every((val) => values2.has(val as any));
}

export const mathOperateNumbers = (operator: string) => {
  switch (operator) {
    case '+':
      return (x: number, y: number) => x + y;
    case '-':
      return (x: number, y: number) => x - y;
    case '*':
      return (x: number, y: number) => x * y;
    case '/':
      return (x: number, y: number) => x / y;
    default:
      return () => 0;
  }
};

export function isSubset(
  firstObj: Record<string, any>,
  secondObj: Record<string, any>
): boolean {
  // Check if all keys from firstObj exist in secondObj
  for (const key in firstObj) {
    if (!(key in secondObj)) {
      return false;
    }

    const firstValue: any = firstObj[key];
    const secondValue: any = secondObj[key];

    // Handle array values
    if (Array.isArray(firstValue) && Array.isArray(secondValue)) {
      // Check if all elements in firstValue array exist in secondValue array
      if (!firstValue.every((item) => secondValue.includes(item))) {
        return false;
      }
    }
    // Handle primitive values (string, number, boolean, etc.)
    else if (firstValue !== secondValue) {
      return false;
    }
  }

  return true;
}

export function findMatchingPartFromReverse(
  str1: string,
  str2: string,
  separator: string
): string {
  const parts = str2.split(separator);
  // Traverse from the end to find the last matching part
  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i] === str1) {
      // Join from the start up to and including the matched part
      return parts.slice(0, i + 1).join(separator);
    }
  }
  // Return empty string if no match found
  return '';
}

export function convertToMapUsingName<T extends { name: string }>(
  entries: T[]
): GenericMap<T> {
  return entries?.reduce((acc, entry) => {
    acc[entry.name] = entry;
    return acc;
  }, {} as GenericMap<T>);
}

export async function copyTextToClipboard(textToCopy: string, logger: any) {
  try {
    await navigator.clipboard.writeText(textToCopy);
  } catch (err) {
    logger.error('Failed to copy text: ', err);
  }
}

export function convertAllKeysSnakeToCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertAllKeysSnakeToCamelCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      acc[snakeToCamelCase(key)] = convertAllKeysSnakeToCamelCase(value);
      return acc;
    }, {} as any);
  }
  return obj;
}
